import { useEffect, useState } from "react";

import { Grid, IconButton, Popper, styled } from "@mui/material";
import { EditionDeedProps, GeneralCommentsProps, PopupType, ReasonRejectionComment } from "@interfaces/slices";
import { DetalleAbortivo } from "../PopupSegmentos/DetalleAbortivo";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";

import CloseIcon from "@mui/icons-material/Close";
import { setPopupConfig } from "@store/slices/componentsSlice";
import { ComentariosGenerales } from "../PopupSegmentos/ComentariosGenerales";
import { DetalleGeneracionContractuales } from "../PopupSegmentos/DetalleGeneracionContractuales";
import { ComentarioMotivoRechazo } from "../PopupSegmentos/ComentarioMotivoRechazo";
import { EdicionEscritura } from "../PopupSegmentos/EdicionEscritura";

const Arrow = styled("span")(({ theme }) => ({
    position: "absolute",
    width: "3em",
    height: "3em",
    "&::before": {
        content: '""',
        margin: "auto",
        display: "block",
        width: "1em",
        height: "1em",
        borderStyle: "solid",
        transform: "translateY(-50%) rotate(45deg)",
        zIndex: "0",
        border: "0px",
        borderTop: `1px solid ${theme.palette.primary.main}`,
        borderLeft: `1px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.background.paper,
        borderTopLeftRadius: "6px",
    },
}));

/**
 * V2 de `@components/PopupManager.tsx` — misma máquina de estados (Redux
 * `state.components.popup` + `setPopupConfig`) y los mismos segmentos de
 * `PopupSegmentos/*` (aún no migrados a v2 individualmente), pero montado
 * dentro de layouts v2 (`ThemeProvider theme={bpmV2Theme}`). Antes solo
 * `DialogManager` v2 estaba disponible; este completa el par para cualquier
 * subFeature v2 que dispare `setPopupConfig(...)` (p. ej. Contractuales).
 */
export const PopupManager = () => {
    const dispatch = useDispatch();

    const [open, setOpen] = useState(false);
    const [arrowRef, setArrowRef] = useState(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const popupConfig = useSelector((state: RootState) => state.components.popup);

    const popupSection = new Map<PopupType, React.ReactNode>([
        [PopupType.NONE, <></>],
        [PopupType.DETALLE_ABORTIVO, <DetalleAbortivo props={popupConfig.props?.details} />],
        [PopupType.COMENTARIOS, <ComentariosGenerales {...(popupConfig.props as GeneralCommentsProps)} />],
        [PopupType.DETALLE_CONTRACTUALES, <DetalleGeneracionContractuales props={popupConfig.props?.details} />],
        [
            PopupType.COMENTARIOS_MOTIVO_RECHAZO_CONTRACTUALES,
            <ComentarioMotivoRechazo {...(popupConfig.props as ReasonRejectionComment)} />,
        ],
        [PopupType.EDICION_ESCRITURA, <EdicionEscritura {...(popupConfig.props as EditionDeedProps)} />],
    ]);

    const handleOnClosed = () => {
        dispatch(
            setPopupConfig({
                type: PopupType.NONE,
                props: undefined,
                anchorEl: undefined,
                placement: "auto",
                width: "",
            }),
        );
    };

    useEffect(() => {
        if (
            popupConfig.anchorEl &&
            (popupConfig.type === PopupType.DETALLE_ABORTIVO || popupConfig.type === PopupType.DETALLE_CONTRACTUALES)
        ) {
            const tmp = document.querySelector(`#${popupConfig.anchorEl}_status`);
            setAnchorEl(tmp as HTMLElement);
            setOpen(true);
        } else if (
            popupConfig.anchorEl &&
            (popupConfig.type === PopupType.COMENTARIOS ||
                popupConfig.type === PopupType.COMENTARIOS_MOTIVO_RECHAZO_CONTRACTUALES ||
                popupConfig.type === PopupType.EDICION_ESCRITURA)
        ) {
            const tmp = document.querySelector(`#${popupConfig.anchorEl}`);

            setAnchorEl(tmp as HTMLElement);
            setOpen(true);
        } else {
            setOpen(false);
        }
    }, [popupConfig.anchorEl]);

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            disablePortal={true}
            placement={popupConfig.placement}
            sx={{
                backgroundColor: "background.paper",
                borderRadius: 4,
                mt: "15px !important",
                filter: "drop-shadow(5px 8px 10px rgba(0,0,0,0.32))",
                border: "1px solid",
                borderColor: "primary.main",
                zIndex: 1250,
                width: popupConfig.width ?? "60%",
                maxWidth: popupConfig.maxWidth ?? "70%",
            }}
            modifiers={[
                {
                    name: "preventOverflow",
                    enabled: true,
                    options: {
                        altAxis: false,
                        altBoundary: false,
                        tether: true,
                        rootBoundary: "document",
                        padding: 20,
                    },
                },
                {
                    name: "arrow",
                    enabled: true,
                    options: {
                        element: arrowRef,
                    },
                },
            ]}
        >
            <Arrow ref={setArrowRef} />
            <Grid container sx={{ justifyContent: "right", position: "relative" }}>
                <IconButton sx={{ position: "absolute" }} onClick={handleOnClosed}>
                    <CloseIcon color="error" />
                </IconButton>
            </Grid>
            {popupSection.get(popupConfig.type)}
        </Popper>
    );
};
